import { api } from "../lib/axios";

interface SignInPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

import { PaymentMethodData } from "../components/affiliate/PaymentInfoForm";

interface SignUpData {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  /** Optional at signup — affiliates can pick niches later from their profile. */
  niches?: string[];
  country?: string;
  googleId?: string;
  profileImage?: string;
  completionToken?: string;
  /**
   * BrandDetail id of the white-label brand the affiliate signed up through
   * (branded portal hostname, or a brand join link). The backend ignores it
   * unless that brand's white-label is actually live, so sending a stale or
   * ordinary brand id safely degrades to a marketplace signup.
   */
  homeBrandDetailId?: string;
  // New payment methods structure
  paymentMethods?: PaymentMethodData[];
  // Legacy payment methods structure (for backward compatibility)
  legacyPaymentMethods?: {
    paypal?: string;
    cashapp?: string;
    venmo?: string;
    zelle?: string;
    bitcoin?: string;
    wise?: string;
    ethereum?: string;
    solana?: string;
    remitly?: string;
    Etransfer?: string;
  };
}

interface CompleteProfileInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  brandPaymentMethods?: Array<{
    brandId: string;
    brandName: string;
    paymentMethods: Array<{
      id: string;
      name: string;
    }>;
  }>;
  minimumPaymentMethods?: Array<{
    id: string;
    name: string;
    brandsCovered: number;
  }>;
}

export type { CompleteProfileInfo };

interface AuthUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  nicheId: string;
  isActive: boolean;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  accessToken: string;
  refreshToken: string;
}

/** What the backend hands back when a Google sign-in ticket is redeemed. */
export interface GoogleAuthResponse {
  needsOnboarding: boolean;
  googleId: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImage?: string;
  accessToken?: string;
  refreshToken?: string;
  id?: string;
}

export const authService = {
  async signIn(data: SignInPayload) {
    const response = await api.post<AuthUser>("/affiliates/signin", data);

    return {
      user: {
        id: response.data.id,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        role: "AFFILIATE" as const,
        isActive: response.data.isActive,
        createdAt: response.data.createdAt,
      },
      token: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    };
  },

  async signUp(data: SignUpData) {
    const response = await api.post<AuthUser>("/affiliates/signup", data);
    console.log("Raw signup response:", response.data);

    // Ensure we're properly extracting all required fields
    return {
      user: {
        id: response.data.id,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        role: "AFFILIATE" as const,
        isActive: true,
        createdAt: response.data.createdAt,
      },
      token: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    };
  },

  async adminSignIn(data: SignInPayload) {
    const response = await api.post("/admin/signin", data);
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
    }
    return response.data;
  },

  /**
   * Swaps the one-time ticket from the Google redirect for the sign-in result.
   * The identity was verified server-side; see lib/googleSignIn for the flow.
   */
  async redeemGoogleSignInTicket(ticket: string) {
    const response = await api.post<GoogleAuthResponse>(
      "/affiliates/auth/google/ticket",
      { ticket }
    );
    return response.data;
  },

  async getCompleteProfileInfo(token: string): Promise<CompleteProfileInfo> {
    const response = await api.get<CompleteProfileInfo>(
      `/affiliates/complete-profile-info?token=${encodeURIComponent(token)}`
    );
    return response.data;
  },
};
